import * as amplitude from '@amplitude/analytics-browser';
import * as Sentry from '@sentry/react';
import { UserType } from '@inspectify/core';
import { getTreatments } from '@splitsoftware/splitio-redux';
import {
  AuthUser,
  confirmResetPassword,
  fetchAuthSession,
  getCurrentUser,
  resetPassword,
  ResetPasswordOutput,
  signIn,
  SignInInput,
  signInWithRedirect,
  signOut,
} from 'aws-amplify/auth';
import { Hub } from "aws-amplify/utils";
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useGetCurrentUserQuery } from '../gql/generated/queries';
import { snackActions } from '../redux/slices/snackSlice/snack';
import { useAppDispatch } from '../redux/store';
import { LocalStorageKey } from '../vendor/local';
import { captureException } from '../vendor/sentry';
import { splitNames } from '../vendor/splitio';
import { UnknownAction } from '@reduxjs/toolkit';
import { NavigateFunction, useParams } from 'react-router-dom';

export type AppUser = {
  user?: UserType;
  currentContractor?: UserType;
  availableUsers?: UserType[];
  challengeName?: string;
}

interface IAuthProvider {
  userContext: AppUser;

  isReady: boolean;

  setSelectedUserContractor(user: UserType): void;

  forgot(email: string): Promise<ResetPasswordOutput | undefined>;

  login(input: SignInInput, navigate: NavigateFunction): Promise<AppUser | undefined>;

  logout(): Promise<void>;

  reset(email: string, password: string, code: string): Promise<void>;

  googleSignIn(from: string): Promise<void>;
}

export const AuthContext = createContext<IAuthProvider>(null!);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [userContext, setUser] = useState<AppUser>({});
  const [isReady, setIsReady] = useState(false);

  const { data, isError, isLoading } = useGetCurrentUserQuery();
  const dispatch = useAppDispatch();
  const { isu: inspectifySelectedUser } = useParams();

  const setSelectedUserContractor = (currentContractor: UserType | undefined) => {
    if (!currentContractor) return;
    setUser(prev => ({ ...prev, currentContractor }));
    localStorage.setItem(LocalStorageKey.SelectedContractor, currentContractor.id as string);
  }

  const completeLogin = useCallback(async (authUser?: AuthUser) => {
    if (!authUser) {
      return undefined;
    }
    await fetchSession();

    if (isError || !data?.currentUser) {
      await signOut();
      throw new Error('Error fetching user data');
    }

    const identifyEvent = new amplitude.Identify();
    identifyEvent.set('email', data?.currentUser?.email || '');
    identifyEvent.set('contractorId', data?.currentUser?.contractor?.id || '');
    identifyEvent.set('cognitoId', authUser.userId);
    amplitude.setGroup('contractorType', data?.currentUser?.contractor?.contractorType || '');
    amplitude.setGroup('teamId', data?.currentUser?.teamUsers?.[0]?.team?.id || '');
    amplitude.identify(identifyEvent);

    Sentry.setUser({ email: data?.currentUser?.email || '' });

    const lastSelectedContractorId = inspectifySelectedUser || localStorage.getItem(LocalStorageKey.SelectedContractor);
    const currentContractor: UserType = data?.availableUsers
      ?.find(user => user.id === lastSelectedContractorId) as UserType
      || data?.availableUsers?.[0] as UserType;

    const user = {
      user: data?.currentUser as UserType,
      currentContractor,
      availableUsers: data?.availableUsers as UserType[],
    };

    setUser(user);
    setSelectedUserContractor(currentContractor);
    dispatch(getTreatments({ splitNames }) as UnknownAction);

    return user;
  }, [data, isError]);

  //check login status
  useEffect(() => {
    if (isLoading) return;

    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signInWithRedirect":
          getCurrentUser().then(completeLogin).catch(error => {
            dispatch(snackActions.pushError({ name: 'Authentication', message: 'Error fetching user data' }));
            captureException(error, { source: 'getCurrentApiUser' });
          }).finally(() => setIsReady(true));
          break;
        case "signInWithRedirect_failure":
          dispatch(snackActions.pushError({
            message: "An error has occurred during the OAuth flow.",
            name: "Authentication",
            severity: "error"
          }));
          break;
        case "customOAuthState":
          // using localStorage to store the sso redirect url because the page is refreshed after the sso redirect
          // losing the state of the app
          localStorage.setItem(LocalStorageKey.SsoRedirect, payload.data);
          break;
      }
    });

    getCurrentUser().then(completeLogin).catch(console.warn).finally(() => setIsReady(true));

    return unsubscribe;
  }, [isLoading]);

  const login = async (newUser: SignInInput, navigate: NavigateFunction) => {
    const output = await signIn(newUser);
    if (output.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      navigate('/temp-password', { state: { email: newUser.username } });
      return;
    }
    return completeLogin(await getCurrentUser());
  }

  const forgot = async (email: string) => {
    try {
      return resetPassword({ username: email });
    } catch (error) {
      dispatch(snackActions.pushError({ name: 'Authentication', message: 'Error fetching user data' }));
      captureException(error, { source: 'forgotPassword' });
    }
  }

  const reset = async (email: string, password: string, confirmationCode: string) => {
    try {
      await confirmResetPassword({ confirmationCode, options: undefined, username: email, newPassword: password });
    } catch (error) {
      dispatch(snackActions.pushError({ name: 'Authentication', message: 'Error fetching user data' }));
      captureException(error, { source: 'resetPassword' });
    }
  }

  const googleSignIn = async (from: string) => {
    try {
      return signInWithRedirect({ provider: 'Google', customState: from || '/' });
    } catch (error) {
      dispatch(snackActions.pushError({ name: 'Authentication', message: 'Error fetching user data' }));
      captureException(error, { source: 'googleSignIn' });
    }
  }

  const logout = async () => {
    localStorage.removeItem(LocalStorageKey.SelectedContractor);
    localStorage.removeItem(LocalStorageKey.TokenExp);
    localStorage.removeItem(LocalStorageKey.Token);
    localStorage.removeItem(LocalStorageKey.MfaToken)
    localStorage.removeItem(LocalStorageKey.MfaTokenExp)
    setUser({});
    amplitude.reset();
    Sentry.setUser(null);
    return signOut();
  };

  const value = {
    userContext,
    isReady,
    login,
    logout,
    forgot,
    reset,
    googleSignIn,
    setSelectedUserContractor,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

/**
 * https://docs.amplify.aws/gen1/react/build-a-backend/auth/manage-user-session/
 */
export const fetchSession = async (forceRefresh = false) => {
  try {
    const session = await fetchAuthSession({ forceRefresh });
    const { idToken } = session.tokens ?? {};
    // debugger
    localStorage.setItem(LocalStorageKey.Token, idToken?.toString() ?? '');
    localStorage.setItem(LocalStorageKey.TokenExp, ((idToken?.payload?.exp || 0) * 1000).toString());
  } catch (error) {
    captureException(error, { source: 'fetchSession' });
  }
}
